import { Box } from '@mui/material';
import { DocumentStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloDescriptionModeToggle, PaloFormSection } from '@synergy/components-palo';
import { PaloThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolFormattedMessage } from '@synergy/components';
import { errorsToTouch } from '@synergy/utils';
import { useEffect, useMemo } from 'react';

import { DetailPositionsTableContainer } from '../components/detail-positions-table-container';
import { FormikGroupItem } from '../../../../../../common/components/new-stepper/formik-group-item';
import { InvoiceItem, isInvoiceProductItem } from '../../../../models/invoice-item';
import { InvoiceProductItem } from '../../../../models/invoice-product-item';
import { InvoiceSteps } from '../../../models/invoice-steps';
import { PaloInvoicePositionTools } from '../../../palo/palo-invoice-position-tools';
import { PaloInvoicePositionsWatcher } from '../../../palo/palo-invoice-positions-watcher';
import { PositionsFormValues } from '../../../models/positions-form-values';
import { StepData } from '../../../../../../common/components/new-stepper/step-data';
import { allTaxRatesSelector } from '../../../../../configuration/finance/tax-rates/store/tax-rates-selector';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { defaultCurrencySelector } from '../../../../../../common/selectors/currency-selectors';
import { fetchInvoiceNumberSequenceSupplierHistory } from '../../../../../configuration/finance/invoice-number-sequence/store/invoice-number-sequence-actions';
import { invoiceDetailOverviewDataSelector } from '../../../store/overview/invoice-detail-overview-selectors';
import {
    invoiceDetailPositionsBaselineSelector,
    invoiceDetailPositionsDataSelector,
} from '../../../store/positions/invoice-detail-positions-selectors';
import { positionsValidationSchema } from '../positions-validation-schema';
import { submitPositions } from '../../../store/positions/invoice-detail-positions-actions';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../../store/use-thunk-dispatch';
import { withFirstPosition, withoutBlankPositions } from '../../../../../../common/components/palo/palo-first-position';

/**
 * The blank row "Zeile hinzufügen" adds, as the first position of an invoice
 * without one. Its account arrives with its article, as in every blank row
 * (#2263). A fixed id and position, so the form and the state it is compared
 * against hold the same row.
 */
const blankInvoiceItem = (currency: string) => (): InvoiceItem =>
    InvoiceProductItem.fromPlainObject({
        id: 1,
        position: 0,
        name: {},
        quantity: 1,
        price: 0,
        discount: 0,
        orderUnit: '',
        isBestPrice: false,
        currency,
    });

const withFirst = (values: PositionsFormValues, editable: boolean, currency: string): PositionsFormValues =>
    values.allItems
        ? { ...values, allItems: withFirstPosition(values.allItems, editable, blankInvoiceItem(currency)) }
        : values;

/** A blank row is no position and does not reach the invoice. */
const withoutBlankRow = (data: StepData<PositionsFormValues>): StepData<PositionsFormValues> =>
    data.values.allItems
        ? {
              ...data,
              values: { ...data.values, allItems: withoutBlankPositions(data.values.allItems, isInvoiceProductItem) },
          }
        : data;

export const PaloInvoiceDetailPositions = () => {
    const dispatch = useThunkDispatch();
    const positionsData = useAppSelector(invoiceDetailPositionsDataSelector);
    // The step as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(invoiceDetailPositionsBaselineSelector);
    const overviewData = useAppSelector(invoiceDetailOverviewDataSelector);
    const defaultCurrency = useAppSelector(defaultCurrencySelector);
    const taxRates = useAppSelector(allTaxRatesSelector);
    const locale = useAppSelector(bestLocaleSelector);
    const appLocale = useAppSelector(state => state.currentLocale);
    const currency = useAppSelector(invoiceDetailOverviewDataSelector).values.currency ?? defaultCurrency;
    // Only after a save was refused: before that an empty table is simply a
    // mask that has not been filled in yet (#2206, R-134).
    const missingPosition = useAppSelector(
        state =>
            !!state.invoiceDetail.common.saveRefused &&
            0 === withoutBlankPositions(positionsData.values.allItems ?? [], isInvoiceProductItem).length
    );
    const status = overviewData.values.status;
    const editable = !status || DocumentStatus.Created === status;

    // The first position stands there while an editable invoice has none. The
    // form and the state it is compared against both pass through here, so
    // the blank row is no unsaved input (#2308). Positions not loaded yet are
    // left alone: a save sends nothing for them, and must keep it so.
    const initialValues = useMemo(
        () => withFirst(positionsData.values, editable, currency),
        [positionsData.values, editable, currency]
    );
    const dirtyBaseline = useMemo(
        () => baseline && withFirst(baseline, editable, currency),
        [baseline, editable, currency]
    );

    useEffect(() => {
        if (overviewData.values.supplierAccount?.id) {
            dispatch(fetchInvoiceNumberSequenceSupplierHistory({ supplierId: overviewData.values.supplierAccount.id }));
        }
    }, [dispatch, overviewData]);

    return (
        <PaloThemeProvider>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <FormikGroupItem
                    groupItemId={InvoiceSteps.Positions}
                    dirtyBaseline={dirtyBaseline}
                    initialValues={initialValues}
                    onSubmit={data => dispatch(submitPositions(withoutBlankRow(data)))}
                    validationSchema={positionsValidationSchema}
                    initialErrors={positionsData.errors}
                    initialTouched={errorsToTouch(positionsData.errors)}
                    enableReinitialize={true}
                    validateOnMount={true}
                    isDirty={true}
                >
                    <PaloInvoicePositionsWatcher />
                    {/* The mask's own section, so the positions keep the
                        heading and the spacing of every other block on it —
                        a heading of its own left the table hanging under the
                        one above and against the one below (#2208). */}
                    <PaloFormSection
                        title={<FormattedMessage id="app.header.positions" />}
                        action={<PaloDescriptionModeToggle />}
                    >
                        {/* The table brings its own height and pages its rows; a
                            viewport that cuts it off would scroll inside the mask. */}
                        <Box
                            data-palo-anchor="invoice-positions"
                            sx={
                                missingPosition
                                    ? {
                                          border: `1px solid ${tokens.color.crit}`,
                                          borderRadius: tokens.radius.small + 'px',
                                          padding: '6px',
                                      }
                                    : undefined
                            }
                        >
                            <DetailPositionsTableContainer
                                toolbar={<PaloInvoicePositionTools />}
                                currency={currency}
                                locale={locale}
                                appLocale={appLocale}
                                taxRates={taxRates}
                            />
                            {missingPosition && (
                                <Box sx={{ marginTop: '6px', fontSize: '11px', color: tokens.color.crit }}>
                                    <FormattedMessage id="app.invoices.detail.saveNeedsPosition" />
                                </Box>
                            )}
                        </Box>
                    </PaloFormSection>
                </FormikGroupItem>
            </Box>
        </PaloThemeProvider>
    );
};
